import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, LOGO_URL, errMsg } from "../lib/api";
import { useAuth } from "../context/Store";

export default function Auth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const savedRef = params.get("ref") || localStorage.getItem("vearn_ref") || "";
  const [form, setForm] = useState({ name: "", email: "", loginIdentifier: "", phone: "", password: "", confirmPassword: "", referral_code: savedRef });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetStep, setResetStep] = useState("request");
  const [resetForm, setResetForm] = useState({ identifier: "", otp: "", newPassword: "", confirmPassword: "" });
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [sponsorName, setSponsorName] = useState("");
  const [referralStatus, setReferralStatus] = useState("");
  const next = params.get("next") || "/";

  useEffect(() => {
    const code = form.referral_code.trim().toUpperCase();
    if (!code) {
      setSponsorName("");
      setReferralStatus("");
      return undefined;
    }
    let active = true;
    setSponsorName("");
    setReferralStatus("checking");
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/referral/${encodeURIComponent(code)}`);
        if (active) {
          setSponsorName(data.sponsor_name);
          setReferralStatus("valid");
        }
      } catch {
        if (active) {
          setSponsorName("");
          setReferralStatus("invalid");
        }
      }
    }, 350);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [form.referral_code]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && form.password !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      if (mode === "forgot") {
        if (resetStep === "request") {
          await api.post("/auth/forgot-password/request-otp", { identifier: resetForm.identifier });
          setResetStep("verify");
          toast.success("If an account matches, a reset code has been sent to its registered contact.");
        } else {
          if (resetForm.newPassword !== resetForm.confirmPassword) {
            toast.error("Passwords do not match");
            return;
          }
          await api.post("/auth/forgot-password/reset", {
            identifier: resetForm.identifier, otp: resetForm.otp, new_password: resetForm.newPassword,
          });
          setForm((current) => ({ ...current, loginIdentifier: resetForm.identifier, password: "" }));
          setResetForm({ identifier: "", otp: "", newPassword: "", confirmPassword: "" });
          setMode("login");
          setResetStep("request");
          toast.success("Password updated. Sign in with your new password.");
        }
      } else if (mode === "login") {
        await login(form.loginIdentifier, form.password);
        toast.success("Welcome back");
        navigate(next);
      } else {
        await register({
          name: form.name, email: form.email, password: form.password,
          phone: form.phone,
          referral_code: form.referral_code || undefined,
        });
        localStorage.removeItem("vearn_ref");
        toast.success("Account created — welcome to Vearn");
        navigate("/");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const input = "mt-2 w-full h-12 rounded-2xl bg-white/5 border border-white/10 px-4 text-sm text-white outline-none focus:ring-2 focus:ring-brand-cyan placeholder:text-white/30";

  return (
    <div className="min-h-screen bg-brand-navy flex items-center justify-center px-4 py-24 relative overflow-hidden" data-testid="auth-page">
      <div className="absolute -top-32 -left-32 h-80 w-80 rounded-full bg-brand-blue/25 blur-[120px]" />
      <div className="absolute -bottom-32 -right-32 h-80 w-80 rounded-full bg-brand-cyan/15 blur-[120px]" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
        className="relative w-full max-w-md rounded-3xl border border-white/10 bg-brand-surface/60 backdrop-blur-xl p-8 md:p-10">
        <Link to="/" className="flex justify-center">
          <div className="bg-white rounded-2xl px-5 py-2.5"><img src={LOGO_URL} alt="Vearn" className="h-9 w-auto" /></div>
        </Link>
        {mode !== "forgot" && <div className="mt-8 grid grid-cols-2 gap-2 rounded-full bg-white/5 p-1">
          {["login", "register"].map((m) => (
            <button key={m} type="button" data-testid={`auth-tab-${m}`} onClick={() => setMode(m)}
              className={`h-10 rounded-full text-sm font-semibold transition-colors ${mode === m ? "brand-gradient-bg text-white" : "text-white/50 hover:text-white"}`}>
              {m === "login" ? "Sign in" : "Create account"}
            </button>
          ))}
        </div>}
        {mode === "forgot" ? (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-password-form">
            <div>
              <h1 className="font-display text-2xl font-bold text-white">Reset your password</h1>
              <p className="mt-2 text-sm leading-6 text-white/50">Receive a one-time code using your registered email address or mobile number.</p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-white/40">Email or mobile number</label>
              <input data-testid="reset-identifier" type="text" inputMode="text" autoComplete="username" required
                readOnly={resetStep === "verify"} value={resetForm.identifier}
                onChange={(e) => setResetForm({ ...resetForm, identifier: e.target.value })}
                placeholder="you@example.com or 9790007557" className={`${input} ${resetStep === "verify" ? "opacity-60" : ""}`} />
            </div>
            {resetStep === "verify" && <>
              <p className="text-xs leading-5 text-brand-cyan">If an account matches, a code was sent. It expires in 10 minutes.</p>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-white/40">6-digit reset code</label>
                <input data-testid="reset-otp" required inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6}
                  value={resetForm.otp} onChange={(e) => setResetForm({ ...resetForm, otp: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  placeholder="Enter code" className={input} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-white/40">New password</label>
                <div className="relative">
                  <input data-testid="reset-new-password" type={showResetPassword ? "text" : "password"} required minLength={8} maxLength={100}
                    autoComplete="new-password" value={resetForm.newPassword} onChange={(e) => setResetForm({ ...resetForm, newPassword: e.target.value })}
                    placeholder="At least 8 characters" className={`${input} pr-14`} />
                  <button type="button" onClick={() => setShowResetPassword((visible) => !visible)} aria-label={showResetPassword ? "Hide new password" : "Show new password"}
                    className="absolute right-3 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center bg-transparent text-slate-600 transition hover:text-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan">
                    {showResetPassword ? <EyeOff aria-hidden="true" className="h-[18px] w-[18px]" strokeWidth={1.8} /> : <Eye aria-hidden="true" className="h-[18px] w-[18px]" strokeWidth={1.8} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-white/40">Confirm new password</label>
                <input data-testid="reset-confirm-password" type="password" required minLength={8} maxLength={100} autoComplete="new-password"
                  value={resetForm.confirmPassword} onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })}
                  placeholder="Re-enter new password" className={input} />
              </div>
            </>}
            <button type="submit" disabled={busy || (resetStep === "verify" && resetForm.otp.length !== 6)} data-testid="reset-submit"
              className="w-full h-12 rounded-full brand-gradient-bg text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-[0_8px_30px_rgba(41,98,255,0.4)] hover:scale-[1.01] transition-transform disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {resetStep === "request" ? "Send reset code" : "Reset password"}
            </button>
            <button type="button" onClick={() => { setMode("login"); setResetStep("request"); }} className="w-full py-2 text-sm font-semibold text-white/55 hover:text-brand-cyan">
              Back to sign in
            </button>
          </form>
        ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          {mode === "register" && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-white/40">Full name</label>
              <input data-testid="auth-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Priya Kumar" className={input} />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-white/40">{mode === "login" ? "Email or mobile number" : "Email"}</label>
            <input data-testid={mode === "login" ? "auth-identifier" : "auth-email"} type={mode === "login" ? "text" : "email"} autoComplete={mode === "login" ? "username" : "email"} required value={mode === "login" ? form.loginIdentifier : form.email} onChange={(e) => setForm({ ...form, [mode === "login" ? "loginIdentifier" : "email"]: e.target.value })} placeholder={mode === "login" ? "you@example.com or 9790007557" : "you@example.com"} className={input} />
          </div>
          {mode === "register" && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-white/40">Mobile number</label>
              <input data-testid="auth-phone" type="tel" autoComplete="tel" required minLength={10} maxLength={24} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="9790007557" className={input} />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-white/40">Password</label>
            <div className="relative w-full">
              <input data-testid="auth-password" type={showPassword ? "text" : "password"} autoComplete={mode === "login" ? "current-password" : "new-password"} required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className={`${input} pr-14`} />
              <button type="button" data-testid="auth-password-toggle" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} title={showPassword ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center bg-transparent text-slate-600 transition hover:text-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan">
                {showPassword ? <EyeOff aria-hidden="true" className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} /> : <Eye aria-hidden="true" className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} />}
              </button>
            </div>
          </div>
          {mode === "register" && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-white/40">Confirm password</label>
              <div className="relative w-full">
                <input data-testid="auth-confirm-password" type={showConfirmPassword ? "text" : "password"} autoComplete="new-password" required minLength={6} value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} placeholder="Re-enter your password" className={`${input} pr-14`} />
                <button type="button" data-testid="auth-confirm-password-toggle" onClick={() => setShowConfirmPassword((visible) => !visible)} aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"} aria-pressed={showConfirmPassword} title={showConfirmPassword ? "Hide confirm password" : "Show confirm password"} className="absolute right-3 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center bg-transparent text-slate-600 transition hover:text-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan">
                  {showConfirmPassword ? <EyeOff aria-hidden="true" className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} /> : <Eye aria-hidden="true" className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} />}
                </button>
              </div>
            </div>
          )}
          {mode === "register" && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-white/40">Referral code (optional)</label>
              <input data-testid="auth-referral" value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })} placeholder="Referral or reserved slot code" className={input} />
              {referralStatus === "checking" && <p className="mt-2 text-xs text-white/40">Checking referral code…</p>}
              {referralStatus === "valid" && <p data-testid="auth-sponsor-name" className="mt-2 text-xs text-emerald-300">Sponsor: {sponsorName}</p>}
              {referralStatus === "invalid" && <p className="mt-2 text-xs text-rose-300">Referral code not found.</p>}
            </div>
          )}
          <button type="submit" disabled={busy} data-testid="auth-submit"
            className="w-full h-12 rounded-full brand-gradient-bg text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-[0_8px_30px_rgba(41,98,255,0.4)] hover:scale-[1.01] transition-transform disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "login" ? "Sign in" : "Create my account"}
          </button>
          {mode === "login" && <button type="button" data-testid="forgot-password-link"
            onClick={() => { setResetForm({ identifier: form.loginIdentifier, otp: "", newPassword: "", confirmPassword: "" }); setResetStep("request"); setMode("forgot"); }}
            className="w-full py-2 text-sm font-semibold text-brand-cyan hover:text-white">Forgot password?</button>}
        </form>
        )}
        <p className="mt-6 text-center text-xs text-white/30">Shop • Earn • Grow Together</p>
      </motion.div>
    </div>
  );
}
